import { Dispatch, Fragment, SetStateAction, useContext, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { Done } from '@mui/icons-material'
import { Button, Chip, DialogContent, Divider, Grid, Typography, useTheme } from '@mui/material'
import { GridRowSelectionModel } from '@mui/x-data-grid-pro'
import { Form, Formik, FormikProps } from 'formik'
import { useSnackbar } from 'notistack'
import * as Yup from 'yup'

import { useHost } from '../../providers/app-provider'
import { DeviceTableRow } from '../../routes/accounts/components/devices-tab-panel/types'
import AccountsContext from '../../routes/accounts/context'
import Api from '../../utils/api'
import DialogWithTitle from '../dialog-with-title'
import FormikDropdown from '../formik-dropdown'
import FormikTextField from '../formik-textfield'

import useStyles from './index.styles'

type ReturnChipType = 'RMA' | 'RTI'

interface DeviceReturnModalProps {
  returnDevices: DeviceTableRow[] | IDeviceAPIObject[]
  open: boolean
  onClose: () => void
  setSelectedIds?: Dispatch<SetStateAction<GridRowSelectionModel>>
  refetchView: () => void
}

export interface IDeviceReturnFormValues {
  returnReason: string
  rmaNumber: string
  trackingNumber: string
  returnNotes: string
  returnType: string
  deviceIds: number[]
}

const rmaReasons = [
  { id: 'CustomerDamagedDevice', name: 'Customer Damaged Device' },
  { id: 'CustomerInvoluntaryOptOut', name: 'Customer Involuntary OptOut' },
  { id: 'CustomerVoluntaryOptOut', name: 'Customer Voluntary OptOut' },
  { id: 'EquipmentUpgrade', name: 'Equipment Upgrade' },
  { id: 'ExtraUnit', name: 'Extra Unit' },
  { id: 'FaultyEquipNonWarranty', name: 'Faulty Equipment - Non-Warranty' },
  { id: 'FaultyEquipWarranty', name: 'Faulty Equipment - Warranty' },
  { id: 'FaultyEquipWarrantyOverride', name: 'Faulty Equipment - Warranty Override' },
  { id: 'IncorrectItem', name: 'Incorrect Item' },
  { id: 'ShippingDamage', name: 'Shipping Damage' },
].sort((a, b) => a.name.localeCompare(b.name))

const DeviceReturnModal = (props: DeviceReturnModalProps) => {
  const { returnDevices, open, onClose, setSelectedIds, refetchView } = props
  const classes = useStyles
  const theme = useTheme()
  const { enqueueSnackbar } = useSnackbar()
  const { setDeviceAction, setReturnModalStatus } = useContext(AccountsContext)
  const auth = useAuth()
  const host = useHost()

  const [selectedChip, setSelectedChip] = useState<ReturnChipType>('RMA')

  const initialValues = {
    rmaNumber: '',
    trackingNumber: '',
    returnReason: '',
    returnNotes: '',
  } as IDeviceReturnFormValues

  const schema = Yup.object().shape({
    trackingNumber: Yup.string().required('Required').max(20, 'Must be less than 20 characters'),
  })

  const handleSubmitReturn = async (values: IDeviceReturnFormValues) => {
    if (!host || !auth.user?.access_token) return
    const payload = {
      deviceIds: returnDevices.map((item) => item.id),
      notes: values.returnNotes,
      reason: values.returnReason,
      returnType: selectedChip,
      rmaNumber: values.rmaNumber,
      trackingNumber: values.trackingNumber,
    }

    const res = await Api.post(`${host}/api/v2/central/devices/return`, payload)
    if (res.ok) {
      setReturnModalStatus({ open: false, devices: [] })
      setDeviceAction(undefined)
      if (setSelectedIds) {
        setSelectedIds({ type: 'include', ids: new Set() })
      }
      refetchView()
      onClose()
      enqueueSnackbar(`Return ${res?.body?.rmaNumber ?? payload.rmaNumber} successfully created and all selected devices have been deactivated.`)
    } else {
      enqueueSnackbar('Something went wrong and return was not created', { variant: 'error' })
    }
  }

  return (
    <DialogWithTitle open={open} title="Hardware Return" setOpen={onClose}>
      <DialogContent>
        <Formik initialValues={initialValues} validationSchema={schema} onSubmit={handleSubmitReturn}>
          {({ isValid, dirty }: FormikProps<IDeviceReturnFormValues>) => (
            <Form>
              <Grid container={true} spacing={2}>
                <Grid size={12}>
                  <Typography variant="subtitle2">Return Type</Typography>
                </Grid>

                <Grid container={true} spacing={2} size={12}>
                  <Grid>
                    <Chip
                      label="RMA"
                      onClick={() => setSelectedChip('RMA')}
                      deleteIcon={<Done />}
                      onDelete={selectedChip === 'RMA' ? () => {} : undefined}
                      className={selectedChip === 'RMA' ? 'chipSelected' : ''}
                      sx={{ ...(selectedChip === 'RMA' ? classes.chipSelected(theme) : {}) }}
                    />
                  </Grid>

                  <Grid>
                    <Chip
                      label="RTI"
                      onClick={() => setSelectedChip('RTI')}
                      deleteIcon={<Done />}
                      onDelete={selectedChip === 'RTI' ? () => {} : undefined}
                      className={selectedChip === 'RTI' ? 'chipSelected' : ''}
                      sx={{ ...(selectedChip === 'RTI' ? classes.chipSelected(theme) : {}) }}
                    />
                  </Grid>
                </Grid>

                <Grid container={true} spacing={2} size={12}>
                  <Grid size={6}>
                    <FormikTextField label="RMA #" fullWidth={true} name="rmaNumber" placeholder="Optional" />
                  </Grid>

                  <Grid size={6}>
                    <FormikTextField label="Tracking #" fullWidth={true} name="trackingNumber" required={true} />
                  </Grid>
                </Grid>

                <Grid size={12}>
                  <FormikDropdown name="returnReason" label="Return Reason" required={false} items={rmaReasons} size="medium" />
                </Grid>

                <Grid size={12}>
                  <FormikTextField name="returnNotes" label="Additional Notes" fullWidth={true} multiline={true} rows={2} />
                </Grid>

                <Grid
                  container={true}
                  size={12}
                  sx={{
                    ml: 1,
                  }}
                >
                  <Grid size={12}>
                    <Typography variant="subtitle2">Return Items ({returnDevices.length})</Typography>
                    <Divider />
                  </Grid>

                  <Grid sx={classes.returnItemsList} size={12}>
                    {returnDevices.map((device) => (
                      <Fragment key={device.id}>
                        <Typography
                          variant="body2"
                          sx={{
                            my: 1,
                          }}
                        >
                          {device.vanity} • {device.uniqueId} • {device.nodeType.vanityName}
                        </Typography>
                        <Divider />
                      </Fragment>
                    ))}
                  </Grid>
                </Grid>

                <Grid
                  container={true}
                  size={12}
                  sx={{
                    justifyContent: 'flex-end',
                    gap: 2,
                    mt: 1,
                  }}
                >
                  <Button variant="outlined" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained" disabled={!returnDevices.length || !isValid || !dirty}>
                    Create Return
                  </Button>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </DialogWithTitle>
  )
}

export default DeviceReturnModal
